import { useQuery } from '@tanstack/react-query';
import type { User } from '@shared/schema';

export type AuthUser = Omit<User, 'password' | 'passwordResetToken' | 'passwordResetExpires'> & {
  /** Set when the session user carries an app-specific role (e.g. league COACH). */
  role?: string | null;
  /** Integration-partner login. Null for every other account. */
  partnerId?: string | null;
};

export function useAuth() {
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      const response = await fetch('/api/auth/me', { credentials: 'include' });

      if (response.status === 401) {
        return null;
      }

      if (!response.ok) {
        const error = new Error('Failed to fetch authenticated user');
        (error as any).status = response.status;
        throw error;
      }

      return response.json();
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Always refetch on mount to ensure fresh auth state after login/logout
    // This is critical for detecting auth state changes after navigation
    refetchOnMount: true,
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}
